import React from 'react';

/**
 * RecipientRoleSelect - A reusable role selection dropdown for recipients
 *
 * Props:
 * - value: Current role value ('Signer' or 'CC')
 * - onChange: Function to handle role change
 * - isTemplateFlow: Boolean - if true, only one Signer is allowed
 * - recipients: Array of all recipients
 * - currentIndex: Index of the current recipient
 * - className: Optional additional CSS classes
 */
const RecipientRoleSelect = ({
  value = 'Signer',
  onChange,
  isTemplateFlow = false,
  recipients = [],
  currentIndex = 0,
  className = ''
}) => {
  // Count existing signers (excluding current recipient)
  const signerCount = recipients.filter((r, idx) => 
    idx !== currentIndex && r.role === 'Signer'
  ).length;

  // In template flow, disable Signer option if there's already a signer (and current isn't one)
  const isSignerDisabled = isTemplateFlow && signerCount >= 1 && value !== 'Signer';

  // Handle change with validation
  const handleChange = (e) => {
    const newRole = e.target.value;
    
    // Prevent selecting Signer in template flow if limit reached
    if (isTemplateFlow && newRole === 'Signer' && signerCount >= 1) {
      return;
    }
    
    onChange(newRole);
  };

  return (
    <div className="relative">
      <select
        value={value}
        onChange={handleChange}
        className={`w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500 ${
          isSignerDisabled ? 'bg-gray-50' : ''
        } ${className}`}
      >
        <option
          value="Signer"
          disabled={isSignerDisabled}
          className={isSignerDisabled ? 'text-gray-400' : ''}
        >
          Signer {isSignerDisabled ? '(limit reached)' : ''}
        </option>
        <option value="CC">CC (Carbon Copy)</option>
      </select>

      {/* Helper text for template flow */}
      {isTemplateFlow && isSignerDisabled && (
        <p className="text-xs text-amber-600 mt-1">
          Only one Signer is allowed in templates
        </p>
      )}
    </div>
  );
};

export default RecipientRoleSelect;

